// ─── THESIS SHARE CARD ────────────────────────────────────────────────────────
// The exportable artifact. Rendered twice from one definition: scaled down as a
// preview inside the share modal, and at full size off-screen for the PNG
// capture.
//
// ⚠⚠ FIXED 600px WIDE, ALWAYS. This is designed for export, not for display —
// the capture has to produce the same image on a phone and a desktop, so nothing
// here is responsive and nothing reads the viewport. The modal scales the whole
// card with a CSS transform rather than letting it reflow, because a reflowed
// card would export differently depending on who was sharing it.
//
// ⚠ No external fonts, no images, no network. html2canvas rasterises what the
// DOM already has; anything still loading at capture time is missing from the
// PNG. Everything below is text, borders and inline SVG.

import { C, font, NousIcon } from "../core";

export const SHARE_CARD_WIDTH = 600;

const ALIGN = {
  ALIGNS: { icon: "✅", color: C.green, label: "ALIGNS" },
  CHALLENGES: { icon: "❌", color: C.red, label: "CHALLENGES" },
  NEUTRAL: { icon: "➖", color: C.textDim, label: "NEUTRAL" },
};

const SCORE_COLOR = {
  "STRONGLY ALIGNED": C.green,
  ALIGNED: C.green,
  MIXED: C.orange,
  CHALLENGED: C.red,
  "STRONGLY CHALLENGED": C.red,
  WATCHING: C.textDim,
};

const rule = { height: 1, background: C.border, margin: "14px 0" };

function shortDate(iso) {
  const ms = Date.parse(iso || "");
  if (!Number.isFinite(ms)) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric",
    }).format(new Date(ms));
  } catch { return "—"; }
}

// ⚠ The first resolved window wins. A card has room for one number per signal,
// and the earliest resolved one is the only one guaranteed to exist on a young
// thesis — showing "21d: pending" would spend the line on an absence.
function firstOutcome(sig) {
  for (const w of ["24h", "48h", "7d", "21d"]) {
    const v = sig[`outcome_${w}`];
    if (v != null) {
      return { window: w, pct: Number(v), ok: sig[`direction_correct_${w}`] };
    }
  }
  return null;
}

// ⚠ Matched on signal_id when the notification carries one, falling back to
// ticker+window. The monitor now sends signal_id; the fallback covers a
// notification written before it did, which is still in a 24h Redis list.
function isHighlighted(sig, highlight) {
  if (!highlight || !sig) return false;
  if (highlight.signal_id != null && sig.id != null) {
    return String(highlight.signal_id) === String(sig.id);
  }
  return String(highlight.ticker || "") === String(sig.ticker || "");
}

export function ThesisShareCard({ card, highlight, pull_quote, scale = 1 }) {
  if (!card) return null;
  const sigs = Array.isArray(card.top_signals) ? card.top_signals : [];
  const counts = card.signal_counts || { ALIGNS: 0, CHALLENGES: 0, NEUTRAL: 0 };
  const scoreCol = SCORE_COLOR[card.score_label] || C.textDim;

  return (
    <div
      style={{
        width: SHARE_CARD_WIDTH,
        // ⚠⚠ zoom, NOT transform: scale(). Both shrink the card, but a
        // transformed element still occupies its FULL unscaled height in flow,
        // so the modal would reserve 900px of blank space under a 540px
        // preview. zoom scales the layout box too. The export copy never sets
        // this — html2canvas rasterises real layout, and capturing a scaled
        // preview would produce a 360px image instead of a 600px one.
        zoom: scale === 1 ? undefined : scale,
        background: "#0B0E14",
        border: `1px solid ${C.border}`,
        padding: 28,
        fontFamily: font,
        color: C.text,
        fontSize: 13,
        lineHeight: 1.6,
        boxSizing: "border-box",
      }}
    >
      {/* ── brand ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <NousIcon size={32} />
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "0.14em",
                        color: C.green }}>
            QUANT SIGNAL
          </div>
          <div style={{ fontSize: 10, letterSpacing: "0.22em", color: C.textDim }}>
            THESIS LEDGER
          </div>
        </div>
      </div>

      <div style={rule} />

      {/* ── who and what ── */}
      <div style={{ fontSize: 11, color: C.textDim }}>
        Authored by {card.author_display}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: C.green, lineHeight: 1.25,
                    margin: "4px 0 6px" }}>
        {card.thesis_name}
      </div>
      <div style={{ fontSize: 11, color: C.textMid, letterSpacing: "0.06em" }}>
        {String(card.direction || "").toUpperCase()} ·{" "}
        {String(card.style || "").toUpperCase()} ·{" "}
        {String(card.timeframe || "").toUpperCase()}
      </div>
      <div style={{ fontSize: 10, color: C.textDim, marginTop: 2 }}>
        Created {shortDate(card.created_at)}
      </div>

      {card.note_excerpt && (
        <>
          <div style={rule} />
          <div style={{ fontStyle: "italic", color: C.textMid, fontSize: 12.5 }}>
            “{card.note_excerpt}”
          </div>
          {/* ⚠ The seal date is the claim the card is actually making: the
              thinking is dated earlier than the outcomes beside it. */}
          {card.note_locked_at && (
            <div style={{ fontSize: 10, color: C.textDim, marginTop: 6 }}>
              Sealed {shortDate(card.note_locked_at)} — unedited since
            </div>
          )}
        </>
      )}

      {/* ── the platform's own words ──
          ⚠ AFTER the authored note, never before it. The note is what the user
          claimed at authorship; this is what the platform said afterwards, and
          the order is the argument the card is making. Reversed, the card leads
          with a machine quoting itself.
          ⚠ Rendered ONLY when a pull quote exists. An empty bordered block on a
          card with no brief would read as a section that failed to load. */}
      {pull_quote && (
        <>
          <div style={rule} />
          <div style={{ borderLeft: `2px solid ${C.green}`, paddingLeft: 8 }}>
            <div style={{ fontStyle: "italic", color: C.text, fontSize: 12.5 }}>
              “{pull_quote}”
            </div>
            <div style={{ fontSize: 10, color: C.textDim, marginTop: 6,
                          textAlign: "right" }}>
              — Quant Signal Intelligence Brief
            </div>
          </div>
        </>
      )}

      <div style={rule} />

      {/* ── signals ── */}
      <div style={{ fontSize: 10, letterSpacing: "0.18em", color: C.textDim,
                    marginBottom: 8 }}>
        TOP SIGNALS
      </div>
      {sigs.length === 0 && (
        <div style={{ color: C.textDim, fontSize: 12 }}>
          No signals yet — the platform is watching this basket.
        </div>
      )}
      {sigs.map((sig, i) => {
        const a = ALIGN[String(sig.thesis_alignment || "").toUpperCase()] || ALIGN.NEUTRAL;
        const out = firstOutcome(sig);
        const win = isHighlighted(sig, highlight);
        return (
          <div key={sig.id || i} style={{
            marginBottom: 10,
            // ⚠⚠ THE WHOLE REASON THE CARD IS WORTH SHARING. Without this the
            // signal that actually resolved sits anonymously among two others
            // and the reader has to work out what the post is about.
            ...(win ? {
              border: `1px solid ${C.green}`, borderRadius: 3,
              padding: 10, background: "#0d1a12",
            } : null),
          }}>
            {win && (
              <div style={{ fontSize: 10, fontWeight: 700, color: C.green,
                            letterSpacing: "0.14em", marginBottom: 4 }}>
                ★ VERIFIED WIN
              </div>
            )}
            <div>
              <strong>{sig.ticker}</strong>
              <span style={{ color: C.textDim }}> · {sig.signal_label}</span>
            </div>
            <div style={{ fontSize: 11, color: C.textDim }}>
              {shortDate(sig.detected_at)}
              {sig.price_at_detection != null
                ? ` · $${Number(sig.price_at_detection).toFixed(2)}`
                : ""}
            </div>
            <div style={{ marginTop: 3, display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{
                fontSize: 9.5, fontWeight: 700, color: a.color,
                border: `1px solid ${a.color}`, borderRadius: 10, padding: "1px 7px",
              }}>
                {a.icon} {a.label}
              </span>
              {out && (
                <span style={{ fontSize: 11, color: out.ok ? C.green : C.red,
                               fontWeight: win ? 700 : 400 }}>
                  {out.window}: {out.pct > 0 ? "+" : ""}{out.pct.toFixed(1)}%{" "}
                  {out.ok ? "✓" : "✗"}{win && out.ok ? " CORRECT" : ""}
                </span>
              )}
            </div>
          </div>
        );
      })}

      <div style={rule} />

      {/* ── score ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, letterSpacing: "0.18em", color: C.textDim }}>
          THESIS SCORE
        </span>
        <span style={{
          fontSize: 13, fontWeight: 700, color: scoreCol,
          border: `1px solid ${scoreCol}`, borderRadius: 3, padding: "2px 10px",
          letterSpacing: "0.08em",
        }}>
          {card.score_label || "WATCHING"}
        </span>
        <span style={{ fontSize: 11, color: C.textDim }}>
          {counts.ALIGNS} aligning · {counts.CHALLENGES} challenging
        </span>
      </div>

      <div style={rule} />

      {/* ── provenance ── */}
      {/* ⚠⚠ "PLATFORM VERIFIED" MEANS EXACTLY ONE THING: the signal data came
          from this platform rather than being typed in by the author. It is not
          a claim about the thesis, the author, or the returns. There is no
          external verification system behind it and the wording must never
          drift into implying one. */}
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "flex-end", gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.green,
                        letterSpacing: "0.1em" }}>
            PLATFORM VERIFIED ✓
          </div>
          <div style={{ fontSize: 10, color: C.textDim, marginTop: 3 }}>
            Signal intelligence · Not investment advice
          </div>
        </div>
        {card.public_url && (
          <div style={{ fontSize: 10.5, color: C.textMid, textAlign: "right" }}>
            {card.public_url}
          </div>
        )}
      </div>
    </div>
  );
}

export default ThesisShareCard;
